import {
  Component,
  inject,
  ChangeDetectionStrategy,
  signal,
  OnInit,
  OnDestroy,
  DestroyRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { shouldOfferPwaInstallUi, isIosDevice } from './pwa-runtime.util';
import {
  wasPwaInstallBannerDismissedRecently,
  markPwaInstallBannerDismissed,
} from './pwa-install.storage';

/** Chromium install prompt (not in lib.dom by default). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

@Component({
  selector: 'app-pwa-install-banner',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './pwa-install-banner.component.html',
  styleUrl: './pwa-install-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PwaInstallBannerComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  visible = signal(false);
  /** iOS / iPadOS: istruzioni manuali (Condividi → Aggiungi a Home). */
  iosMode = signal(false);
  /** Android con evento beforeinstallprompt (HTTPS + criteri PWA). Mostra pulsante Installa. */
  nativeInstallAvailable = signal(false);
  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.syncVisibility());

    window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    window.addEventListener('appinstalled', this.onAppInstalled);

    this.syncVisibility();
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', this.onAppInstalled);
  }

  private readonly onBeforeInstallPrompt = (e: Event): void => {
    e.preventDefault();
    this.deferredPrompt = e as BeforeInstallPromptEvent;
    this.syncVisibility();
  };

  private readonly onAppInstalled = (): void => {
    this.deferredPrompt = null;
    this.visible.set(false);
  };

  private syncVisibility(): void {
    if (typeof window === 'undefined') return;
    if (!shouldOfferPwaInstallUi()) {
      this.visible.set(false);
      return;
    }
    if (wasPwaInstallBannerDismissedRecently()) {
      this.visible.set(false);
      return;
    }
    const onAuthRoute =
      this.router.url.startsWith('/login') ||
      this.router.url.startsWith('/forgot-password') ||
      this.router.url.startsWith('/invite') ||
      this.router.url.startsWith('/forms/');
    if (onAuthRoute) {
      this.visible.set(false);
      return;
    }
    const ios = isIosDevice();
    this.iosMode.set(ios);
    this.nativeInstallAvailable.set(!ios && this.deferredPrompt !== null);
    // Su molti setup di sviluppo (ng serve, http://IP-LAN) Chrome non emette mai beforeinstallprompt:
    // mostriamo comunque il banner con istruzioni manuali su Android.
    this.visible.set(true);
  }

  dismiss(): void {
    markPwaInstallBannerDismissed();
    this.visible.set(false);
  }

  async install(): Promise<void> {
    if (this.iosMode()) {
      return;
    }
    const ev = this.deferredPrompt;
    if (!ev) return;
    await ev.prompt();
    await ev.userChoice.catch(() => undefined);
    this.deferredPrompt = null;
    this.visible.set(false);
  }
}
